<template>
  <div class="base-container" :class="{ 'base-container--no-mobile-padding': noMobilePadding }">
    <div
      class="base-container__inner"
      :class="[`base-container__inner--size-${size}`]"
      :style="{ '--base-container-max-width': maxWidth }"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
  /**
   * The page widths content is constrained to. Exported so anything that has to line up
   * with a container — a full-bleed section drawing its own panel, for instance — reads the
   * width from here instead of repeating the number and drifting out of alignment later.
   */
  export const containerWidths = {
    base: '77rem',
    medium: '72rem',
    small: '57rem',
  } as const;

  export type ContainerSize = keyof typeof containerWidths;
</script>

<script lang="ts" setup>
  const { noMobilePadding = false, size = 'base' } = defineProps<{
    noMobilePadding?: boolean;
    size?: ContainerSize;
  }>();

  // Falls back rather than indexing blindly: an unset Storyblok option arrives as ''.
  const maxWidth = computed(() => containerWidths[size] ?? containerWidths.base);
</script>

<style lang="postcss" scoped>
  .base-container {
    box-sizing: content-box;
    padding: 0 var(--spacing-4);

    &--no-mobile-padding {
      padding: 0;

      @media (--screen-lg) {
        padding: 0 var(--spacing-4);
      }
    }

    /* The modifier class stays as a styling hook; the width itself comes from
       `containerWidths` so there is a single source of truth. */
    &__inner {
      box-sizing: border-box;
      max-width: var(--base-container-max-width);
      margin: auto;
    }
  }
</style>
